import { useEffect, useState, type RefObject } from 'react'

// usePopoverAnchor — returns viewport-fixed pixel coords for a popover
// panel anchored to a trigger button. Required when the trigger lives
// inside an overflow-scrolling ancestor (Discover Row 2 uses
// `overflow-x-auto`, which per CSS spec forces `overflow-y` to `auto`
// too — any absolute-positioned panel rendered as a child of the trigger
// would get clipped by the scroll container). Caller is responsible for
// portaling the panel into document.body with `position: fixed`.

export interface PopoverAnchor {
  /** top of the panel in viewport (px), already including a small gap below the trigger. */
  top: number
  /** left of the panel in viewport (px), aligned to the trigger's left edge by default. */
  left: number
  /** width of the trigger (px) — useful when the panel wants to match its trigger. */
  triggerWidth: number
}

interface Options {
  /** vertical gap (px) between the bottom of the trigger and the top of the panel. */
  gap?: number
  /** horizontal alignment of the panel relative to the trigger. */
  align?: 'left' | 'right'
}

export function usePopoverAnchor(
  triggerRef: RefObject<HTMLElement>,
  open: boolean,
  { gap = 6, align = 'left' }: Options = {},
): PopoverAnchor | null {
  const [anchor, setAnchor] = useState<PopoverAnchor | null>(null)

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setAnchor(null)
      return
    }
    const measure = () => {
      const el = triggerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setAnchor({
        top: rect.bottom + gap,
        left: align === 'right' ? rect.right : rect.left,
        triggerWidth: rect.width,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, triggerRef, gap, align])

  return anchor
}
